import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, IconButton, Tooltip, useTheme } from '@mui/material';
import { ChevronLeft, ChevronRight, EditNote, PersonSearch, QueryStats } from '@mui/icons-material';
import SearchableSelect, { SelectOption } from '../common/SearchableSelect';
import EmptyState from '../common/EmptyState';
import useAutoFetch from '../../hooks/useAutoFetch';
import { getStudentAnalysis } from '../../services/analysisService';
import { enrollmentsApi } from '../../services/sessions/enrollmentsApi';
import { sectionInstancesApi } from '../../services/sessions/sectionInstancesApi';
import { AnalysisScope, pickId } from './useAnalysisScope';
import { useGradeScale } from './useGradeScale';
import TrendSections from './TrendSections';
import { Delta, ExamCountSelect, ResultBody, ScopeFilters, ResultHeading, StatStrip } from './AnalysisParts';
import FilterBar from '../common/FilterBar';
import { pct, summarise, toRows, withColors } from './trend';

interface RosterEntry { _id: string; name: string; roll: number | null; section: string }

const byRoll = (a: RosterEntry, b: RosterEntry) =>
  a.section.localeCompare(b.section) || (a.roll ?? Infinity) - (b.roll ?? Infinity) || a.name.localeCompare(b.name);

const describe = (s: RosterEntry) => [s.section && `Section ${s.section}`, s.roll != null && `Roll ${s.roll}`].filter(Boolean).join(' · ');

const matchAll = (option: SelectOption, input: string) =>
  `${option.label} ${option.secondary ?? ''}`.toLowerCase().includes(input.trim().toLowerCase());

/** One student's scores across the session's exams. Opens on the first student of the class. */
const StudentProgressView: React.FC<{ scope: AnalysisScope }> = ({ scope }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const grades = useGradeScale();
  const [examCount, setExamCount] = useState(10);
  const { sessionId, classId, centerId } = scope;

  const roster = useAutoFetch<RosterEntry[]>(
    sessionId && classId ? `${sessionId}|${classId}` : null,
    async () => {
      // Past sessions keep students in terminal statuses; the list returns every status.
      const [{ data }, sections] = await Promise.all([
        enrollmentsApi.list({ sessionId, classId, limit: 500 }),
        sectionInstancesApi.list({ sessionId, classId }),
      ]);
      const sectionName = new Map(sections.map((s) => [s._id, s.name]));
      return data
        .filter((e: any) => e.studentId)
        .map((e: any) => {
          const student = typeof e.studentId === 'object' ? e.studentId : null;
          return {
            _id: String(student?._id ?? e.studentId),
            name: student?.name ?? e.student?.name ?? '—',
            roll: e.rollNumber ?? null,
            section: (e.sectionInstanceId && sectionName.get(e.sectionInstanceId)) || '',
          };
        })
        .sort(byRoll);
    },
  );

  const students = useMemo(() => roster.data ?? [], [roster.data]);
  const studentId = pickId(scope.raw('student'), students);
  const index = students.findIndex((s) => s._id === studentId);
  const student = students[index];

  const analysis = useAutoFetch(
    studentId ? `${studentId}|${sessionId}|${classId}|${examCount}` : null,
    () => getStudentAnalysis({ studentId, sessionId, classId, centerId, examCount }),
  );

  const series = useMemo(() => {
    const d = analysis.data;
    if (!d) return [];
    return withColors(
      d.subjects.map((s) => ({
        id: s.subjectId,
        name: s.subjectName,
        points: s.examData.map((p) => ({ examId: p.examId, examName: p.examName, examDate: p.examDate, value: p.percentage, absent: p.isAbsent })),
      })),
      { name: 'Overall', points: d.overallTrend.map((p) => ({ examId: p.examId, examName: p.examName, examDate: p.examDate, value: p.percentage })) },
      theme.palette.text.primary,
    );
  }, [analysis.data, theme.palette.text.primary]);

  const rows = useMemo(() => toRows(series), [series]);
  const summary = useMemo(() => summarise(rows, series), [rows, series]);

  const go = useCallback((step: number) => {
    const next = students[index + step];
    if (next) scope.set('student', next._id);
  }, [students, index, scope]);

  const options = useMemo(
    () => students.map((s) => ({ value: s._id, label: s.name, secondary: describe(s) })),
    [students],
  );

  const { className, sessionName } = scope;
  const latest = summary.latest;

  // The roster loads first; until it lands (or fails) the page is still loading.
  const state = roster.error
    ? { ...analysis, error: roster.error, retry: roster.retry }
    : roster.loading && !analysis.data ? { ...analysis, loading: true } : analysis;

  const empty = !classId ? (
    <EmptyState icon={<QueryStats />} title="No classes to analyse" description="Add classes and enrol students to see their progress here." />
  ) : roster.data && students.length === 0 ? (
    <EmptyState icon={<PersonSearch />} title={`No students in ${className}`} description={`Nobody is enrolled in ${className} for ${sessionName}.`} />
  ) : analysis.notFound || (analysis.data && rows.length === 0) ? (
    <EmptyState
      icon={<QueryStats />}
      title={`No results for ${student?.name ?? 'this student'} yet`}
      description="Scores appear here once marks for a published exam are entered."
      action={{ label: 'Enter marks', icon: <EditNote />, onClick: () => navigate('/app/examination/marks') }}
    />
  ) : null;

  return (
    <>
      <FilterBar loading={roster.loading || analysis.loading}>
        <ScopeFilters scope={scope} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <SearchableSelect
            label="Student" value={studentId} clearable={false} minWidth={260}
            onChange={(v) => scope.set('student', v)} options={options} filterFn={matchAll}
            loading={roster.loading} disabled={!students.length}
            placeholder={roster.loading ? 'Loading students…' : 'Search by name, section or roll'}
          />
          <Tooltip title="Previous student">
            <span><IconButton size="small" onClick={() => go(-1)} disabled={index <= 0}><ChevronLeft /></IconButton></span>
          </Tooltip>
          <Tooltip title="Next student">
            <span><IconButton size="small" onClick={() => go(1)} disabled={index < 0 || index >= students.length - 1}><ChevronRight /></IconButton></span>
          </Tooltip>
        </Box>
        <ExamCountSelect value={examCount} onChange={setExamCount} />
      </FilterBar>

      <ResultBody state={state} empty={empty}>
        {student && <ResultHeading title={student.name} subtitle={[className, describe(student), sessionName].filter(Boolean).join(' · ')} />}
        <StatStrip stats={[
          {
            label: 'Latest overall',
            value: pct(latest?.value),
            color: latest ? grades.color(latest.value) : undefined,
            hint: latest ? `${latest.row.examName} · Grade ${grades.letter(latest.value)}` : 'No scored exam yet',
          },
          { label: 'Since previous exam', value: <Delta value={summary.delta} />, hint: summary.delta == null ? 'Needs two exams' : 'Change in overall score' },
          { label: 'Strongest subject', value: summary.best?.series.name ?? '—', hint: summary.best ? `Average ${pct(summary.best.value)}` : undefined },
          { label: 'Needs attention', value: summary.worst?.series.name ?? '—', hint: summary.worst ? `Average ${pct(summary.worst.value)}` : undefined },
        ]} />

        <TrendSections rows={rows} series={series} grades={grades} />
      </ResultBody>

    </>
  );
};

export default StudentProgressView;
